

import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

interface EmailMetric {
  date: string;
  report_type: 'summary' | 'insights';
  total_sent: number;
  successful_sends: number;
  failed_sends: number;
  success_rate: number;
}

interface EmailMetricsTableProps {
  emailMetrics: EmailMetric[];
  loading: boolean;
  error: string | null;
}

export function EmailMetricsTable({ emailMetrics, loading, error }: EmailMetricsTableProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-2" />
        <p className="text-destructive font-medium">Failed to load email metrics</p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!emailMetrics || emailMetrics.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-muted-foreground">No email metrics available for the selected period.</p>
      </div>
    );
  }

  return (
    <Table>
      <TableCaption>Email delivery metrics and performance</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Total Sent</TableHead>
          <TableHead>Delivered</TableHead>
          <TableHead>Failed</TableHead>
          <TableHead>Success Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {emailMetrics.map((metric, index) => (
          <TableRow key={index}>
            <TableCell>{new Date(metric.date).toLocaleDateString()}</TableCell>
            <TableCell>
              <Badge variant={metric.report_type === 'insights' ? 'secondary' : 'outline'}>
                {metric.report_type === 'insights' ? 'Weekly Insights' : 'Weekly Summary'}
              </Badge>
            </TableCell>
            <TableCell>{metric.total_sent}</TableCell>
            <TableCell>
              <div className="flex items-center text-green-600">
                <CheckCircle className="h-4 w-4 mr-2" />
                {metric.successful_sends}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center text-red-600">
                <XCircle className="h-4 w-4 mr-2" />
                {metric.failed_sends}
              </div>
            </TableCell>
            <TableCell>
              <Badge 
                variant={metric.success_rate > 90 ? 'default' : metric.success_rate > 70 ? 'outline' : 'destructive'}
              >
                {metric.success_rate.toFixed(2)}%
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
